import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import WeatherWidget from '../../components/WeatherWidget';
import CustomAppBar from '../../components/AppBar';
import CustomDashboardCard from '../../components/Custom/CustomDashboard';
import { useTranslation } from 'react-i18next';
import api from '../../globals/axios';
import CustomHierarchyTable from '../../components/Custom/CustomHierarchyTable'; // Make sure this is the correct import
import { useNavigate } from 'react-router-dom';
import PieChartData from '../../components/Custom/Charts';
import QuickLinks from '../../components/QuickLinks';
import getQuickLinks from '../../Data/iconsData';

const AreaManagerDashboard = () => {
  const { t } = useTranslation();
  const [areaManagerDetails, setAreaManagerDetails] = useState({});
  const [vles, setVLEs] = useState([]);
  const [areaManagerTargets, setAreaManagerTargets] = useState(null);
  const navigate = useNavigate();

  const fetchAreaManagerDetails = async () => {
    try {
      const response = await api.get('/areamanager/get-area-manager-details/');
      setAreaManagerDetails(response.data);
    } catch (error) {
      console.error('Error Fetching Area Manager Details:', error);
    }
  };

  const fetchLatestTarget = async () => {
    try {
      const response = await api.get('/areamanager/target-progress/');
      setAreaManagerTargets(response.data);
      const transformedVLEs = response.data.vle_progress_details.map(vle => ({
        ...vle,
        farmer_target_display: `${vle.farmers_added}/${vle.farmer_target}`,
        area_target_display: `${vle.total_area_added}/${
          vle.area_target
        } ${t()}`,
      }));

      setVLEs(transformedVLEs);
    } catch (error) {
      console.error('Error Fetching Latest Target:', error);
    }
  };

  useEffect(() => {
    fetchAreaManagerDetails();
    fetchLatestTarget();
  }, []);

  const columns = [
    { label: 'VLE Name', field: 'vle_name', align: 'center' },
    { label: 'District', field: 'vle_address', align: 'center' },
    {
      label: 'Farmers Target',
      field: 'farmer_target_display',
      align: 'center',
    },
    {
      label: 'Area Target (acres)',
      field: 'area_target_display',
      align: 'center',
    },
    {
      label: 'Score',
      field: 'vle_score',
      align: 'center',
    },
  ];

  const farmersPieChartData = {
    title: 'Farmers Target',
    labels: [t('Onboarded'), t('Remaining')],
    datasets: [
      {
        data: [
          areaManagerTargets?.total_farmers_added,
          areaManagerTargets?.farmer_target -
            areaManagerTargets?.total_farmers_added,
        ],
        backgroundColor: ['#75C178', '#CF5C5C'],
        hoverBackgroundColor: ['#75C178', '#CF5C5C'],
      },
    ],
  };

  const areaPieChartData = {
    title: 'Area Target (acres)',
    labels: [t('Mapped'), t('Remaining')],
    datasets: [
      {
        data: [
          areaManagerTargets?.total_area_added,
          areaManagerTargets?.area_target -
            areaManagerTargets?.total_area_added,
        ],
        backgroundColor: ['#75C178', '#CF5C5C'],
        hoverBackgroundColor: ['#75C178', '#CF5C5C'],
      },
    ],
  };

  const handleRowClick = row => {
    navigate(`/area-manager/vle/${row.vle_id}`);
  };

  const quickLinksData = getQuickLinks('area_manager');

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs" style={{ marginTop: 20 }}>
        <WeatherWidget />
        <Box mt={2}>
          <Grid container spacing={1} mt={0.5}>
            <Grid item xs={6}>
              <CustomDashboardCard
                title={t('Total VLEs')}
                data={areaManagerDetails.vle_count || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDashboardCard
                title={t('Farmer count')}
                data={areaManagerDetails.farmer_count || 0.0}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomDashboardCard
                title={t('Total Area')}
                data={areaManagerDetails.total_area || 0.0}
                unit={`${t('acres')}`}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDashboardCard
                title={t('Assigned Programs')}
                data={areaManagerDetails.total_assigned_programs || 0}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDashboardCard
                title={t('Programs Completed')}
                data={areaManagerDetails.completed_programs_count || 0}
              />
            </Grid>
          </Grid>

          {areaManagerTargets && (
            <Card variant="outlined" style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" component="h2">
                  {t('Targets for date')} : {areaManagerTargets.target_date}
                </Typography>
                <Typography variant="body1">
                  {t('Farmers to onboard')} : {areaManagerTargets.farmer_target}
                </Typography>
                <Typography variant="body1">
                  {t('Farmers onboarded')} :
                  {areaManagerTargets.total_farmers_added}
                </Typography>
                <Typography variant="body1">
                  {t('Area to be mapped')} : {areaManagerTargets.area_target}
                  {t('acres')}
                </Typography>
                <Typography variant="body1">
                  {t('Area mapped')} : {areaManagerTargets.total_area_added}
                  {t(' acres')}
                </Typography>
                <Typography variant="body1">
                  {t('Your Score')} : {areaManagerTargets.area_manager_score}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
        <Box mt={2}>
          <CustomHierarchyTable
            columns={columns}
            data={vles}
            onRowClick={handleRowClick}
          />
        </Box>
        {areaManagerTargets && (
          <Box mt={1}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <PieChartData data={farmersPieChartData} t={t} />
              </Grid>
              <Grid item xs={12}>
                <PieChartData data={areaPieChartData} t={t} />
              </Grid>
            </Grid>
          </Box>
        )}
        <Box mt={2}>
          <QuickLinks data={quickLinksData} />
        </Box>
      </Container>
    </>
  );
};

export default AreaManagerDashboard;
